<template>
  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
</template>
<script setup lang="ts">
import { useCurrentUserStore } from "~/stores/userStore";
import { useSettingsStore } from "~/stores/settingsStore";

const currentUserStore = useCurrentUserStore();

// Hydrate settings store from build-time payload so footer/header have data on first paint.
const config = useRuntimeConfig();
const initialSettings = useState<unknown[] | undefined>(
  "settings-initial",
  () => (config.public.initialSettings as unknown[] | undefined) ?? undefined,
);
useSettingsStore().hydrate((initialSettings.value ?? undefined) as never);

// useSiteSettings() fetch and useUnread() are deferred to default layout after first paint (Step 3: LCP optimization).

// Accessibility: set <html lang> and dir from current locale (i18n)
const i18nHead = useLocaleHead({ addSeoAttributes: true });
useHead({
  htmlAttrs: {
    lang: computed(() => i18nHead.value?.htmlAttrs?.lang ?? "en"),
    dir: computed(() => i18nHead.value?.htmlAttrs?.dir),
  },
});

// Performance: no preconnect to API in global head — Lighthouse reports "Unused preconnect" on landing
// because the first API call (e.g. settings) isn’t in the critical path. Add preconnect only in pages/layouts that need it.
useHead({
  link: computed(() => {
    // During prerender, useRequestURL/useRoute throw "instance unavailable". Skip logo preload then.
    let path: string;
    try {
      path = useRequestURL().pathname;
    } catch {
      return [];
    }
    const isIndex = path === "/" || /^\/[a-z]{2}$/.test(path);
    const links: Array<
      | { rel: "preload"; href: string; as: "image"; type?: string; fetchPriority?: "high" }
    > = [];
    if (!isIndex) {
      links.push({
        rel: "preload",
        href: "/images/logos/gfs-group-tradelines-logo.svg",
        as: "image",
        type: "image/svg+xml",
        fetchPriority: "high",
      });
    }
    return links;
  }),
});

onMounted(() => {
  if (!currentUserStore.isTokenPresent) return;

  currentUserStore.fetchCurrentUser();
});
</script>
